import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useFormik, type FormikProps } from 'formik';
import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useUIStore } from '../../stores/ui/ui.store';
import { useUnidadesStore } from '../../stores/unidades/unidades.store';
import generateRandomId from '../../utils/generate-random-id';
import transformToOptions from '../../utils/transform-to-options';
import AppTextField from '../ui/AppTextField';
import ComboBox from '../ui/Combobox';
import TabPanel from '../ui/TabPanel';
import type { ArticuloFormValues } from './articulo.schema';

type EquivalenciasTabProps = {
  value: number;
  formik: FormikProps<ArticuloFormValues>;
  inactivo: boolean;
  loading: boolean;
};

const EquivalenciasTab = ({
  value,
  formik: articuloForm,
  inactivo,
  loading
}: EquivalenciasTabProps) => {
  const [editando, setEditando] = useState(false);
  const { actives: unidades, cargarActivas: cargarUnidadesActivas } = useUnidadesStore(
    (state) => state
  );
  const { showWarningToast } = useUIStore((state) => state);
  const equivForm = useFormik({
    initialValues: {
      id: '',
      idUnidad: '-',
      cantidad: ''
    },
    validationSchema: yup.object({
      id: yup.string(),
      idUnidad: yup.string().min(1, 'Debe seleccionar una unidad'),
      cantidad: yup
        .number()
        .typeError('Debe ser numérico')
        .integer('Debe ser entero')
        .moreThan(0, 'Debe ser mayor a cero')
        .required('Debe proporcionar la equivalencia')
    }),
    onSubmit: (values) => {
      const existe = articuloForm.values.equivalencias.some((x) => {
        return (
          x.idUnidad === values.idUnidad && x.cantidad === +values.cantidad && x.id !== values.id
        );
      });
      if (existe) {
        showWarningToast('La equivalencia ya se encuentra asignada al artículo');
        return;
      }

      let equivalencias = [];

      if (editando) {
        equivalencias = [
          ...articuloForm.values.equivalencias.map((x) =>
            x.id === values.id
              ? {
                  id: values.id,
                  idUnidad: values.idUnidad,
                  cantidad: +values.cantidad
                }
              : x
          )
        ];
      } else {
        equivalencias = [
          ...articuloForm.values.equivalencias,
          {
            id: generateRandomId(),
            idUnidad: values.idUnidad,
            cantidad: +values.cantidad
          }
        ];
      }

      const baseCount = equivalencias.reduce(
        (prev, curr) => (curr.cantidad === 1 ? prev + 1 : prev),
        0
      );
      if (baseCount > 1) {
        showWarningToast('El artículo ya tiene una unidad base');
        return;
      }

      articuloForm.setFieldValue('equivalencias', equivalencias);
      equivForm.resetForm();
      setEditando(false);
    }
  });

  useEffect(() => {
    cargarUnidadesActivas();
  }, []);

  const unidadesOptions = transformToOptions(unidades, { valueDefault: '-' });

  const quitarEquivalencia = (id: string) => {
    articuloForm.setFieldValue(
      'equivalencias',
      articuloForm.values.equivalencias.filter((x) => x.id !== id)
    );
  };

  const editarEquivalencia = (e: any) => {
    setEditando(true);
    equivForm.setValues({
      id: e.id,
      idUnidad: e.idUnidad,
      cantidad: e.cantidad
    });
  };

  const cancelarCambios = () => {
    equivForm.resetForm();
    setEditando(false);
  };

  return (
    <TabPanel value={value} index={1}>
      <Grid container justifyContent="space-around" spacing={1}>
        <Grid size={3}>
          <Stack spacing={1}>
            <ComboBox
              label="Unidad"
              name="idUnidad"
              disabled={loading || inactivo}
              value={equivForm.values.idUnidad}
              onChange={equivForm.handleChange}
              error={equivForm.touched.idUnidad && Boolean(equivForm.errors.idUnidad)}
              helperText={equivForm.touched.idUnidad ? equivForm.errors.idUnidad : ''}
              options={unidadesOptions}
            />
            <AppTextField
              label="Equivalencia"
              name="cantidad"
              placeholder="Equivalencia"
              disabled={loading || inactivo}
              value={equivForm.values.cantidad}
              onChange={equivForm.handleChange}
              onBlur={equivForm.handleBlur}
              error={equivForm.touched.cantidad && Boolean(equivForm.errors.cantidad)}
              helperText={equivForm.touched.cantidad ? equivForm.errors.cantidad : ''}
            />
            {!editando && (
              <Button
                variant="contained"
                disabled={loading || inactivo}
                onClick={() => equivForm.handleSubmit()}
                disableElevation
                fullWidth
              >
                Agregar
              </Button>
            )}
            {editando && (
              <>
                <Button
                  variant="contained"
                  disabled={loading || inactivo}
                  onClick={() => equivForm.handleSubmit()}
                  disableElevation
                  fullWidth
                >
                  Actualizar
                </Button>
                <Button
                  variant="contained"
                  disabled={loading || inactivo}
                  onClick={cancelarCambios}
                  disableElevation
                  fullWidth
                >
                  Cancelar
                </Button>
              </>
            )}
          </Stack>
        </Grid>
        <Grid size={8}>
          <Stack direction="column" component={Paper}>
            <TableContainer>
              <Table sx={{ width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell width={70} />
                    <TableCell width={70} />
                    <TableCell>Unidad</TableCell>
                    <TableCell>Equivalencia</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {articuloForm.values.equivalencias.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => quitarEquivalencia(e.id!)}
                          disabled={inactivo || loading || editando}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="info"
                          onClick={() => editarEquivalencia(e)}
                          disabled={inactivo || loading || editando}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>{e.idUnidad}</TableCell>
                      <TableCell>{e.cantidad}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {articuloForm.touched.equivalencias && Boolean(articuloForm.errors.equivalencias) && (
              <Box p={2}>
                <FormHelperText error={true}>
                  {articuloForm.errors.equivalencias?.toString()}
                </FormHelperText>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </TabPanel>
  );
};

export default EquivalenciasTab;
